import type { ParentType } from '@risksmart-app/domain/src/types/consts';
import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import TreeModel from 'tree-model';

const tree = new TreeModel();

interface ObjectType {
  id: string;
}

const RiskAssessmentResultHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.RiskAssessmentResult,
  children: [{ id: ParentTypes.RiskAssessmentResultImpact }],
};

const AssessmentResults: TreeModel.Model<ObjectType>[] = [
  { id: ParentTypes.TestResult },
  RiskAssessmentResultHierarchy,
  { id: ParentTypes.ObligationAssessmentResult },
  { id: ParentTypes.DocumentAssessmentResult },
];

const AssessmentHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Assessment,
  children: [
    ...AssessmentResults,
    { id: ParentTypes.Issue },
    { id: ParentTypes.Action },
    { id: ParentTypes.AssessmentActivity },
  ],
};

const ImpactHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Impact,
  children: [{ id: ParentTypes.ImpactRating }],
};

const ActionHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Action,
  children: [{ id: ParentTypes.ActionUpdate }],
};

const IssueHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Issue,
  children: [
    { id: ParentTypes.IssueUpdate },
    ActionHierarchy,
    { id: ParentTypes.Cause },
    { id: ParentTypes.Consequence },
    { id: ParentTypes.IssueAssessment },
  ],
};

const IndicatorHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Indicator,
  children: [{ id: ParentTypes.IndicatorResult }],
};

const ControlHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Control,
  children: [
    { id: ParentTypes.TestResult },
    IssueHierarchy,
    ActionHierarchy,
    IndicatorHierarchy,
  ],
};

const ControlGroupHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.ControlGroup,
  children: [ControlHierarchy],
};

const ObligationChangeHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.ObligationChange,
  children: [ActionHierarchy],
};

const ObligationHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Obligation,
  children: [
    ControlHierarchy,
    ...AssessmentResults,
    // COI-627: an Obligation Change owns the actions raised from it. V2's
    // `actions/post.ts` allow-list included `ObligationChange`, but here it was
    // a childless leaf, so `classifyLink` resolved ObligationChange→Action as
    // `sibling`: the action got a `linked_item` row instead of an
    // `action_parent` row, vanished from the Obligation Change's Actions tab,
    // and never inherited the parent's perm_v2 grants.
    ObligationChangeHierarchy,
    { id: ParentTypes.ObligationImpact },
    ActionHierarchy,
    IssueHierarchy,
  ],
};

const RiskHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Risk,
  children: [
    ControlHierarchy,
    ...AssessmentResults,
    ImpactHierarchy,
    { id: ParentTypes.Appetite },
    { id: ParentTypes.Acceptance },
    ActionHierarchy,
    IndicatorHierarchy,
  ],
};

const EntityHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Entity,
  children: [],
};

const ThirdPartyResponseHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.ThirdPartyResponse,
};

const QuestionnaireTemplateVersionHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.QuestionnaireTemplateVersion,
  children: [ThirdPartyResponseHierarchy],
};

const QuestionnaireTemplateHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.QuestionnaireTemplate,
  children: [QuestionnaireTemplateVersionHierarchy],
};

const ThirdPartyHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.ThirdParty,
  children: [
    ThirdPartyResponseHierarchy,
    ControlHierarchy,
    ActionHierarchy,
    IssueHierarchy,
  ],
};

const DocumentHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Document,
  children: [
    ...AssessmentResults,
    ActionHierarchy,
    IssueHierarchy,
    // Documents (policies) own compliance/performance indicators the same
    // way risks and controls do. Without this entry, classifyLink() treats
    // Document↔Indicator as a sibling relationship and routes the link
    // through insertSiblingLinks instead of the well-tested
    // indicator_parent parent-table path, which caused linked indicators
    // to disappear from the Document's Linked Items tab (RSP-4075).
    IndicatorHierarchy,
    {
      id: ParentTypes.DocumentFile,
      children: [{ id: ParentTypes.AttestationRecord }],
    },
  ],
};

// RSP-5374: Issues can be raised under Internal Audit reports/entities and
// Compliance Monitoring assessments — these types are listed in
// `ALLOWED_ISSUE_PARENT_TYPES` (issue-repository.ts). Without these entries,
// classifyLink() resolves the link as `sibling`, the issue create processor's
// validation reads `link.SourceType` as `'issue'` and throws a misleading
// "Invalid parent type: issue" error, blocking creation under these parents.
const InternalAuditReportHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.InternalAuditReport,
  children: [IssueHierarchy],
};

const InternalAuditEntityHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.InternalAuditEntity,
  children: [IssueHierarchy],
};

const OversightAssessmentHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.OversightAssessment,
  children: [IssueHierarchy],
};

// COI-627: Asset was absent from the tree entirely, so every Asset link
// classified as `sibling`. V2's `actions/post.ts` and `issues/post.ts`
// allow-lists both included `Asset`, which made two live product routes dead in
// V3 — an action raised from an Asset silently got no `action_parent` row, and
// an issue raised from an Asset was rejected outright with a misleading
// "Invalid parent type: issue" (the create processor read `link.SourceType`
// off a sibling classification). Asset owns actions and issues only; V2 never
// allowed it as a parent for controls or indicators.
//
// COI-666 — this edge also widens PERMISSIONS, deliberately and with sign-off.
// The tree is transitive, so Asset now has seven descendants (see the pinned
// table in `hierarchy.test.ts`), and `classifyLink` turns a descendant into a
// `parent_child` link on the GENERIC Linked Items route as well as the create
// route. `node_ancestor_insert` walks exactly those rows and the perm_v2
// parent-grant cascade reads `node_ancestor` — so a grant on the Asset now
// reaches a linked Issue or Action and its whole subtree, where before the link
// conferred nothing.
//
// Two things worth stating plainly, because neither is obvious:
//   * This is NOT a V2 behaviour. Asset was absent from the tree, so on V2 an
//     Asset link classified `sibling` and granted nothing.
//   * It changes V2 too, not just V3. `packages/rest-api/src/handlers/
//     linked-item/post.ts` imports this same `link-service`, and
//     `node_ancestor_insert_refresh_trigger` is a database trigger on
//     `risksmart.linked_item`, so it fires whoever writes the row.
//
// Accepted because it makes Asset consistent with every other parent type —
// Risk, Control, Obligation, Document, ThirdParty, InternalAuditReport and
// OversightAssessment have always cascaded grants from the same modal. Do not
// add a parent edge here without re-reading that: an edge is a permission.
const AssetHierarchy: TreeModel.Model<ObjectType> = {
  id: ParentTypes.Asset,
  children: [ActionHierarchy, IssueHierarchy],
};

// COI-671 — the seven legacy issue-subtype node types, as Action parents.
//
// V2's `actions/post.ts` allow-list has always accepted them; V3's did not, and
// they were absent from this tree entirely, so `classifyLink` never returned
// `parent_child` for them and `POST /actions` answered 400. Production holds
// **871 `action_parent` rows under `issue_risk_event`** (one org), so the
// workflow is live on V2 and would break for that org on V3.
//
// THIS IS A PERMISSION CHANGE, and a deliberate one (COI-671 option 1, signed
// off). Read the AssetHierarchy note above first: an edge here is transitive,
// `linkItems` writes a `_parent` row, `node_ancestor_insert` walks it and the
// perm_v2 grant cascade reads `node_ancestor` — so a grant on one of these
// nodes now reaches the actions beneath it and their updates. It changes V2
// too, because `rest-api/handlers/linked-item/post.ts` imports this same
// link-service.
//
// The edge set is deliberately MINIMAL: `[ActionHierarchy]` only, and declared
// as roots rather than nested under Risk / Control / Document / Assessment the
// way `IssueHierarchy` is. These nodes are issues, so mirroring
// `IssueHierarchy` in full (Cause, Consequence, IssueAssessment, IssueUpdate)
// and hanging them off every issue parent would have been the "consistent"
// move — and would have widened the grant surface far beyond the one workflow
// that is actually broken. Only Action needs this: V2's issue, control and
// indicator allow-lists all omit the subtypes, so nothing else can be created
// under one.
//
// The exact surface added is pinned by `hierarchy.test.ts`'s transitive
// descendant table — seven new entries, each exactly `['action',
// 'action_update']`. If that diff ever grows, the widening grew with it.
//
// These can be deleted once a migration rewrites the node ObjectTypes to
// `issue`; see COI-671 option 2.
const LegacyIssueSubtypeHierarchies: TreeModel.Model<ObjectType>[] = [
  ParentTypes.IssueRiskEvent,
  ParentTypes.IssueBreachLog,
  ParentTypes.IssueConsumerDuty,
  ParentTypes.IssueCustomerTrust,
  ParentTypes.IssueGdprBreachLog,
  ParentTypes.IssuePciBreachLog,
  ParentTypes.IssueSarLog,
].map((id) => ({ id, children: [ActionHierarchy] }));

const Root = tree.parse({
  id: 'root',
  children: [
    ...LegacyIssueSubtypeHierarchies,
    EntityHierarchy,
    AssetHierarchy,
    RiskHierarchy,
    ThirdPartyHierarchy,
    QuestionnaireTemplateHierarchy,
    ObligationHierarchy,
    DocumentHierarchy,
    ControlGroupHierarchy,
    AssessmentHierarchy,
    InternalAuditReportHierarchy,
    InternalAuditEntityHierarchy,
    OversightAssessmentHierarchy,
  ],
});

export const isDescendant = (
  ancestorObjectType: ParentType,
  descendantObjectType: ParentType
): boolean => {
  const ancestorNode = Root.first(
    (node) => node.model.id === ancestorObjectType
  );
  if (!ancestorNode) {
    return false;
  }
  const descendantNode = ancestorNode.first((node) => {
    return node.model.id === descendantObjectType;
  });

  return !!descendantNode && descendantNode !== ancestorNode;
};
